import AutoNumeric from "autonumeric";
import { IChartContainer } from "../../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../../models/chart-style/chart-style-settings.model";
import { QuoteType } from "../../models/trade/trade.model";
import { TiChartMenuContainer } from "../../ti-chart-menu.container";
import { IComponent } from "../component.interface";
import { TradeComponent } from "../trade.component";
import { StringHelpers } from "../../helpers/string-helpers";
import { Timeframe } from "../../models/enum/timeframe.enum";

export class TradeOrderTypeComponent implements IComponent {
    public id = StringHelpers.newGuid();

    public value = '';
    public element: HTMLElement;
    private quoteQuantityInputTopRight: AutoNumeric | null = null;
    private base: IChartContainer;
    private tiChartMenuContainer: TiChartMenuContainer;
    private changeCallbacks: Array<Function> = [];

    private tradeComponent: TradeComponent | null = null;
    private upButton: HTMLElement | null = null;
    private downButton: HTMLElement | null = null;
    private editing = false;

    constructor(el: HTMLElement, base: IChartContainer, tiChartMenuContainer: TiChartMenuContainer) {
        this.element = el;
        this.element.id = this.id;
        this.upButton = this.element.findFirst('button.up');
        this.downButton = this.element.findFirst('button.down');
        this.base = base;
        this.tiChartMenuContainer = tiChartMenuContainer;
        
        this.subscribeChartDataService();
        this.onClick();
        this.onBodyClick();
    }

    public onChange(callback: Function) {
        if (callback)
            this.changeCallbacks.push(callback);
    }

    public focusInput() {
        this.quoteQuantityInputTopRight.node().focus();
    }

    public getNumber(): number {
        return this.quoteQuantityInputTopRight.getNumber();
    }

    public editOff() {
        this.editing = false;
    }

    public editOn() {
        this.editing = true;
    }

    private notifyChangeCallbacks() {
        for (const callback of this.changeCallbacks) {
            callback();
        }

        const components = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
        for (const c of components) {
            if (this.id != c.id) {
                c.syncValue();
            }
        }
    }

    private subscribeChartDataService() {
        if (this.base.tiChartSeries?.chartStyleSettings) {
            this.setPropertiesFromChartStyles();
        }
        this.base.chartService.subscribeChartStyleData((data: ChartStyleSettingsModel) => {
            if (this.element?.parentElement?.classList.contains('trade-ui-group')) {
                if (data.chartSettingsPopupModel?.displayBrowserChartExistingBuySellButtons) {
                    this.element?.parentElement?.classList.add('on');
                    this.element.classList.remove('hidden');
                } else {
                    this.element?.parentElement?.classList.remove('on');
                    this.element.classList.add('hidden');
                }
            }

            if (!this.tradeComponent)
                this.setPropertiesFromChartStyles();
        });
        this.base.chartService.subscribeChartRequestingNewSymbolData((symbol: string, timeframe: Timeframe) => {
            this.editing = false;
        });
    }

    private setPropertiesFromChartStyles() {
        this.tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
        if (!this.tradeComponent) {
            return;
        }

        const quoteQuantity = this.tradeComponent.getQuoteQuantity();
        this.quoteQuantityInputTopRight = new AutoNumeric(this.element.findFirst('input'), quoteQuantity, {
            decimalPlaces: 0
        });

        this.quoteQuantityInputTopRight?.node().addEventListener('keyup', () => {
            this.tradeComponent.setQuoteQuantity(this.quoteQuantityInputTopRight.getNumber());
            this.editing = true;
            this.notifyChangeCallbacks();
        });

        this.tradeComponent.onChange(() => {
            this.syncValue();
        });

        this.upButton?.addEventListener('click', () => {
            const value = this.quoteQuantityInputTopRight.getNumber() + 1;
            this.quoteQuantityInputTopRight.set(value);
            this.tradeComponent.setQuoteQuantity(value);
            this.editing = true;
            this.notifyChangeCallbacks();
        });

        this.downButton?.addEventListener('click', () => {
            const value = this.quoteQuantityInputTopRight.getNumber() - 1;
            this.quoteQuantityInputTopRight.set(value);
            this.tradeComponent.setQuoteQuantity(value);
            this.editing = true;
            this.notifyChangeCallbacks();
        });

        this.syncValue();
    }

    public syncValue() {
        const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
        if (!tradeComponent) {
            return;
        }

        if (this.editing) {
            return;
        }
        
        const quoteType = tradeComponent.getQuoteType();
        if (quoteType == QuoteType.Dollars) {
            this.setValue(this.element.findFirst('li[data-value="Dollars"]'));
        } else if (quoteType == QuoteType.Shares) {
            this.setValue(this.element.findFirst('li[data-value="Shares"]'));
        }

        const quoteQuantity = tradeComponent.getQuoteQuantity();
        this.quoteQuantityInputTopRight?.set(quoteQuantity);
        
        this.value = quoteType;
    }

    private setValue(el: HTMLElement | null) {
        if (!el) return;

        const pLabel = this.element.querySelector<HTMLElement>('p span');
        pLabel?.setInnerHtml(el.innerHTML);
        this.element.removeClass('active');

        this.hideOthers();
        const allItems = Array.from(this.element.querySelectorAll('li'));
        allItems.forEach((x: HTMLElement) => {
            x.removeClass('active');
        });
        el.addClass('active');

        this.value = el.dataset.value;
    }

    private setTradeComponentQuoteType() {
        const tradeComponent = this.base.getComponentsByType(TradeComponent)[0] as TradeComponent;
        if (!tradeComponent) {
            return;
        }

        tradeComponent.setQuoteType(this.value as QuoteType);
    }

    private onClick() {
        const pLabel = this.element.querySelector('p');
        pLabel?.addEventListener('click', (event) => {
            event.stopPropagation();

            this.hideOthers();
            this.element.classList.toggle('active');

            const show = this.element.classList.contains('active');
            this.toggle(show);
        });

        const allItems = Array.from(this.element.querySelectorAll('li'));
        allItems.forEach((el: HTMLElement) => {
            if (el.classList.contains('active')) {
                this.value = el.dataset.value;
            }

            el.addEventListener('click', (event) => {
                event.stopPropagation();

                this.editing = true;
                this.setValue(el);
                this.setTradeComponentQuoteType();
                this.notifyChangeCallbacks();
            });
        });
    }

    private hideOthers() {
        const components = this.base.getComponentsByType(TradeOrderTypeComponent) as Array<TradeOrderTypeComponent>;
        for (const c of components) {
            if (this.id != c.id) {
                c.element.classList.remove('active');
            }
        }
    }
    
    private onBodyClick() {
        this.base.domEvents.onBodyClick(this.id, (e: any) => {
            if (typeof e.target.closest === 'undefined') {
                return;
            }

            const element = e.target.classList.contains('.trade-order-type') ? e.target : e.target.closest('.trade-order-type');
            if (!element || this.element.id != element.id) {
                this.element.classList.remove('active');
            }
        });
    }

    private toggle(show: boolean) {
        this.base.hideTooltip();

        if (!this.tiChartMenuContainer) return;
        this.tiChartMenuContainer.displayTimeframe = false;
        this.tiChartMenuContainer.displayTools = false;
        this.tiChartMenuContainer.displayIndicator = false;
        this.tiChartMenuContainer.displayChartType = false;
        this.tiChartMenuContainer.displayTradeOrderType = show;
        this.tiChartMenuContainer.toggleControls();
    }
}